import { useState } from 'react';
import { ChevronDown, Store } from 'lucide-react';
import { useEnrolledTenants } from '@/hooks/use-enrolled-tenants';
import { useTenant } from '@/hooks/use-tenant';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useI18nContext } from '@/i18n/i18n-react';

export function TenantSwitcher() {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const { data: tenants } = useEnrolledTenants();
  const [open, setOpen] = useState(false);

  const noTenants = !tenants || tenants.length <= 1;

  const handleTenantClick = (tenantSlug: string) => {
    setOpen(false);
    window.open(`/${tenantSlug}/dashboard`, '_blank');
  };

  return (
    <SidebarMenu className="w-auto">
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              className="group-data-[collapsible=icon]:!p-0 disabled:opacity-1 hover:bg-transparent active:bg-transparent data-[state=open]:hover:bg-transparent"
              disabled={noTenants}
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarImage src={tenant.iconUrl} alt={tenant.name} />
                <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <Store className="size-4" />
                </AvatarFallback>
              </Avatar>
              {!noTenants && <ChevronDown />}
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-56 p-0"
            align="start"
            side={'bottom'}
            sideOffset={4}
          >
            <Command>
              <CommandInput placeholder={LL.tenantSwitcher.switchTenant()} />
              <CommandList className="max-h-[200px]">
                <CommandEmpty>No store found.</CommandEmpty>
                <CommandGroup>
                  {(tenants ?? []).map((t) => (
                    <CommandItem
                      key={t.id}
                      value={t.name}
                      onSelect={() => handleTenantClick(t.slug)}
                      className="cursor-pointer"
                    >
                      <Avatar className="mr-2 size-6 rounded-md">
                        <AvatarImage
                          src={t.iconUrl}
                          alt={t.name}
                          className="rounded-lg"
                        />
                        <AvatarFallback className="rounded-md">
                          <Store className="size-3" />
                        </AvatarFallback>
                      </Avatar>
                      {t.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
